import React, { useRef, useState } from 'react';
import { register } from '../services/auth.service';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';
import Feedback from '../components/Feedback';
import ModalForm from '../components/Modal';
import Terms from './Terms';

const Register: React.FC = () => {
  const [inviteCode, setInviteCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState([] as string[]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [challengeResponseCode, setChallengeResponseCode] = useState<string>();
  const [showLoginButton, setShowLoginButton] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const navigate = useNavigate();

  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const { isLoggedIn } = useAuth();

  const validTurnstyle = (e: string) => {
    setChallengeResponseCode(e);
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setErrors(['Please accept the Terms of Use']);
      return;
    }
    setLoading(true);
    const result = await register(
      firstName,
      lastName,
      emailAddress,
      password,
      confirmPassword,
      challengeResponseCode ?? '',
      inviteCode
    );
    if (result.success && result.data) {
      if (result.data.requireConfirmedAccount) {
        setMessage('Please check your emails to confirm your account');
      } else {
        setShowLoginButton(true);
        setMessage(
          'You have registered.  You can now log in with these credentials'
        );
      }
    } else {
      setErrors(result.errors);
      setLoading(false);
      turnstileRef.current?.reset();
      turnstileRef.current?.render();
      setChallengeResponseCode('');
    }
  };

  if (isLoggedIn) {
    return <></>;
  }

  const formContents = (
    <form onSubmit={handleLogin} className="login-form">
      <input
        type="text"
        placeholder="First Name"
        required
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
      />
      <input
        type="text"
        placeholder="Last Name"
        required
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
      />
      <input
        type="text"
        placeholder="Email Address"
        required
        value={emailAddress}
        onChange={(e) => setEmailAddress(e.target.value)}
      />
      <input
        type="password"
        placeholder="Password"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <input
        type="password"
        placeholder="Confirm Password"
        required
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
      />
      <input
        type="text"
        placeholder="Invite Code"
        required
        value={inviteCode}
        onChange={(e) => setInviteCode(e.target.value)}
      />
      <div className="checkbox-container">
        <input
          type="checkbox"
          id="acceptedTerms"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
        />
        <label htmlFor="acceptedTerms">
          {' '}
          I agree to the{' '}
          <Button type="Link" onClick={() => setShowTerms(true)}>
            Terms of Use
          </Button>
        </label>
      </div>
      <Turnstile
        siteKey={`${process.env.REACT_APP_TURNSTYLE_SITE}`}
        onSuccess={(e) => validTurnstyle(e)}
        options={{ theme: 'light', appearance: 'always' }}
        ref={turnstileRef}
      />
      <Feedback messages={errors} type="error" />
      <div className="button-container">
        <Button
          isLoading={loading}
          isSubmit={true}
          disabled={!challengeResponseCode}
        >
          Register
        </Button>
        <Button
          type="Outline"
          className="cancel-button"
          onClick={() => navigate('/')}
        >
          Cancel
        </Button>
      </div>
    </form>
  );

  const messageContents = (
    <div className="login-form">
      <Feedback message={message} type="success" />
      <div className="button-container single-button">
        {!showLoginButton && (
          <Button
            type="Outline"
            className="action-button"
            onClick={() => navigate('/')}
          >
            Back
          </Button>
        )}
        {showLoginButton && (
          <Button onClick={() => navigate('/Account/Login')}>Login</Button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <h2>Create an account</h2>
      {message ? messageContents : formContents}
      {showTerms && (
        <ModalForm onClose={() => setShowTerms(false)} fullScreen={true}>
          <Terms />
        </ModalForm>
      )}
    </div>
  );
};

export default Register;
